import { useState } from "react";
import { Check, Loader2, ChevronDown, ChevronRight, ShieldCheck } from "lucide-react";

/** Bandeau d'analyse renforcee : montre que l'expert a verifie, sans exposer sa mecanique. */
export const DeepThinkingPanel = ({ deep, agentName, index = 0 }) => {
  const [open, setOpen] = useState(false);
  if (!deep) return null;

  const running = deep.phase && deep.phase !== "done";
  const steps = deep.steps || [];
  const checks = steps.length || 2;
  const doneChecks = steps.filter((s) => s.done).length;
  const sources = [];
  const seen = new Set();
  steps.forEach((s) => (s.sources || []).forEach((src) => {
    if (src?.url && !seen.has(src.url)) {
      seen.add(src.url);
      sources.push(src);
    }
  }));

  return (
    <div
      className="mb-3 rounded-lg overflow-hidden"
      data-testid={`deep-thinking-panel-${index}`}
      style={{ border: "1px solid var(--gold-soft, #E9DCC4)", background: "var(--gold-bg, #FBF6EC)" }}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={running && sources.length === 0}
        data-testid={`deep-thinking-toggle-${index}`}
        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left disabled:cursor-default"
      >
        <span
          className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
          style={{ background: "var(--ink, #16233B)" }}
        >
          {running ? (
            <Loader2 className="w-3.5 h-3.5 text-white animate-spin" />
          ) : (
            <ShieldCheck className="w-3.5 h-3.5" style={{ color: "var(--gold, #B5894A)" }} />
          )}
        </span>
        <span className="flex-1 min-w-0">
          <span
            className="block text-[0.58rem] uppercase font-semibold"
            style={{ color: "var(--gold-dark, #8C6635)", letterSpacing: "0.2em" }}
          >
            Analyse renforcee
          </span>
          <span
            className="block text-sm font-semibold"
            style={{ color: "var(--ink, #16233B)", fontFamily: "var(--serif, Fraunces)" }}
          >
            {running
              ? `${agentName} verifie les donnees a jour avant de te repondre${doneChecks ? ` (${doneChecks}/${checks})` : ""}`
              : `Reponse verifiee sur ${sources.length || "plusieurs"} source${sources.length > 1 ? "s" : ""} a jour`}
          </span>
        </span>
        {!running && sources.length > 0 && (
          open
            ? <ChevronDown className="w-4 h-4 shrink-0" style={{ color: "var(--gold, #B5894A)" }} />
            : <ChevronRight className="w-4 h-4 shrink-0" style={{ color: "var(--gold, #B5894A)" }} />
        )}
      </button>

      {running && (
        <div className="px-3 pb-3">
          <div className="h-1 rounded-full overflow-hidden relative" style={{ background: "#EFE4CE" }}>
            {doneChecks === 0 ? (
              <div
                className="h-full absolute inset-y-0 w-1/3 animate-[deepSweep_1.8s_ease-in-out_infinite]"
                style={{ background: "linear-gradient(90deg, transparent, #B5894A, transparent)" }}
              />
            ) : (
              <div
                className="h-full transition-all duration-700"
                style={{
                  width: `${Math.round((doneChecks / checks) * 100)}%`,
                  background: "var(--gold, #B5894A)",
                }}
              />
            )}
          </div>
          <p className="text-[0.68rem] mt-2" style={{ color: "#8A7A5F" }}>
            Cette question demande de la precision : l'expert prend le temps de croiser des donnees recentes.
          </p>
        </div>
      )}

      {open && !running && sources.length > 0 && (
        <div className="px-3 pb-3 space-y-1.5" data-testid={`deep-sources-${index}`}>
          {sources.slice(0, 10).map((s, j) => (
            <a
              key={j}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-xs px-2.5 py-1.5 rounded hover:underline"
              style={{ background: "#fff", border: "1px solid var(--gold-soft, #E9DCC4)", color: "var(--ink, #16233B)" }}
            >
              <Check className="w-3 h-3 shrink-0" style={{ color: "var(--teal, #1F6F6B)" }} />
              <span className="truncate">{(s.title || s.url).replace(/^https?:\/\//, "").slice(0, 70)}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};
